import { useEffect, useMemo } from 'react';
import { allBindings, registerBindings } from '../keys/keymap';
import './ShortcutsHelp.css';

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform);

function prettyKey(k: string): string {
  return k
    .replace(/Mod/g, IS_MAC ? '⌘' : 'Ctrl')
    .replace(/Shift/g, '⇧')
    .replace(/Alt/g, IS_MAC ? '⌥' : 'Alt')
    .replace(/ArrowLeft/g, '←')
    .replace(/ArrowRight/g, '→')
    .replace(/ArrowUp/g, '↑')
    .replace(/ArrowDown/g, '↓')
    .replace(/\+(?!$)/g, ' ');
}

export default function ShortcutsHelp({ onClose }: { onClose: () => void }) {
  // Group registered bindings for display; recompute on mount only.
  const groups = useMemo(() => {
    const map = new Map<string, { keys: string[]; description: string }[]>();
    for (const b of allBindings()) {
      if (b.id === 'help.close') continue;
      const list = map.get(b.group) ?? [];
      list.push({ keys: b.keys, description: b.description });
      map.set(b.group, list);
    }
    return [...map.entries()];
  }, []);

  useEffect(
    () =>
      registerBindings([
        {
          id: 'help.close',
          keys: ['Escape'],
          scope: 'global',
          group: 'Help',
          description: 'Close this overlay',
          run: onClose,
        },
      ]),
    [onClose],
  );

  return (
    <div className="shortcuts-backdrop" onClick={onClose}>
      <div className="shortcuts" onClick={(e) => e.stopPropagation()}>
        <div className="shortcuts-head">
          <h2>Keyboard shortcuts</h2>
          <button className="ghost icon" title="Close (Esc)" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="shortcuts-groups">
          {groups.map(([group, items]) => (
            <section key={group}>
              <h3>{group}</h3>
              <table>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i}>
                      <td className="shortcuts-keys">
                        {it.keys.map((k) => (
                          <kbd key={k}>{prettyKey(k)}</kbd>
                        ))}
                      </td>
                      <td>{it.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
